import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { IconCloseLarge, IconWhiteMan } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import ImgInCircle from 'components/UI/ImgInCircle/ImgInCircle'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { useChangeAccount } from 'helpers/hooks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    addLinkedAccount,
    confirmLinkedAccount,
    getLinkedAccounts
} from 'services/endpoints/settings/account/profileSettings'

export const ConnectAnotherAccount = () => {
    const [modalOpen, setModalOpen] = useState(false)
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [mailVerifiaction, setMailVerification] = useState('')
    const [loggedIn, setLoggedIn] = useState(false)
    const [error, setError] = useState('')
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    const { changeAccount } = useChangeAccount()

    const connectAccount = useMutation(
        async () => await addLinkedAccount({ email: username, password }),
        {
            onSuccess: function () {
                error && setError('')
                setLoggedIn(true)
            },
            onError: function (data: any) {
                setError(t(data.response?.data.message, { ns: 'error' }))
            }
        }
    )

    const confirmAccountConnection = useMutation(
        async () =>
            await confirmLinkedAccount({
                email: username,
                password,
                code: mailVerifiaction
            }),
        {
            onSuccess: function () {
                setLoggedIn(false)
                setModalOpen(false)
                setUsername('')
                setPassword('')
                setMailVerification('')
                error && setError('')
                addToast('success', t('accountSuccessfullyLinked'))
                queryClient.invalidateQueries('linkedAccounts')
            },
            onError: function () {
                setError(t('error:invalidVerificationCode'))
                addToast('error', t('error:errorSomethingWentWrong'))
            }
        }
    )

    const { data, isLoading: isLoadingLinkedAccounts } = useQuery(
        'linkedAccounts',
        getLinkedAccounts
    )

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('account')}
                    title={t('connectAnotherAccount')}
                />
            }
        >
            <div
                className={`connect_another_account_modal ${
                    modalOpen && 'connect_another_account_modal_open'
                }`}
            >
                <div className='connect_another_account_close'>
                    <div
                        onClick={() => {
                            setModalOpen(false)
                        }}
                    >
                        <IconCloseLarge />
                    </div>
                </div>
                {!loggedIn ? (
                    <>
                        <div className='connect_another_account_modal_title'>
                            {t('connectAnotherAccount')}
                        </div>
                        <div className='connect_another_account_modal_desc'>
                            {t('connectASecondaryAccount')}
                        </div>
                        <InputCard
                            type='text'
                            label={t('username')}
                            value={username}
                            changeFn={(val: string) => {
                                setUsername(val)
                            }}
                        />
                        <InputCard
                            type='password'
                            label={t('password')}
                            value={password}
                            changeFn={(val: string) => {
                                setPassword(val)
                            }}
                        />
                        {error && (
                            <div className='connect_another_account_modal_error'>
                                {error}
                            </div>
                        )}
                        <Button
                            text={t('login')}
                            color='black'
                            font='mont-14-normal'
                            height='4'
                            width='14'
                            clickFn={() => connectAccount.mutate()}
                        />
                    </>
                ) : (
                    <>
                        <div className='connect_another_account_modal_title'>
                            {t('emailVerification')}
                        </div>
                        <div className='connect_another_account_modal_desc'>
                            {t(
                                'connectASecondaryAccountForEasySwitchingBetweenAccounts'
                            )}
                        </div>
                        <InputCard
                            type='email'
                            label={t('codeFromEmail')}
                            value={mailVerifiaction}
                            changeFn={(val: string) => {
                                setMailVerification(val)
                            }}
                        />
                        {error && (
                            <div className='connect_another_account_modal_error'>
                                {error}
                            </div>
                        )}
                        <Button
                            text={t('submit')}
                            color='black'
                            font='mont-14-normal'
                            height='4'
                            width='14'
                            clickFn={() => confirmAccountConnection.mutate()}
                        />
                    </>
                )}
            </div>

{!isLoadingLinkedAccounts &&
    data?.map((account: any) => {
        return (
            <ActionCard
                key={account.id}
                icon={
                    account?.avatar?.url ? (
                        <ImgInCircle type='small'>
                            <img
                                src={account.avatar.url}
                                alt={t('avatar')}
                            />
                        </ImgInCircle>
                    ) : (
                        <div className='profileholder__profile__placeholder connect_another_account_profile_placeholder'>
                            <IconWhiteMan />
                        </div>
                    )
                }
                iconSize='small'
                clickFn={() => {
                    changeAccount.mutate({
                        linked_id: account.id
                    })
                }}
                subtext={
                    <span
                        style={{
                            fontSize: '12px',
                            color: '#B0B0B0'
                        }}
                    >
                        {t('username')}
                    </span>
                }
                description={
                    <span
                        style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#0D1444'
                        }}
                    >
                        {account.username}
                    </span>
                }
            />
        )
    })}
    <div className='connect_another_account_button_wrapper'>
            <Button
                text={'Add Account'}
                color='blue'
                font='mont-14-normal'
                height='4'
                width='14'
                clickFn={() => setModalOpen(true)}
            />
            </div>
        </WithHeaderSection>
    )
}
